import React, { useState, useEffect, useRef, useMemo } from 'react';
import MapPanel from "../basic/MapPanel";
import MapLegend from '../specific/MapLegend';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import { BlurCircularTwoTone } from '@mui/icons-material';
import {
    buildColoredMapPathsPerWork,
    createMapValueLegends,
    getHistoryPointCoords,
    getReportWorksData,
    isPlantioStatusIniciado,
    isPulvStatusIniciado,
    parseMapNumericValue,
} from '../../usecases/mapPathUtils';

const shouldIncludePlantioItem = (item) => isPlantioStatusIniciado(item?.stat);
const shouldIncludePulvItem = (item) => isPulvStatusIniciado(item?.stat);

const resolveHahHistory = (workData) => {
    const histPlantio = workData?.[2]?.histPlantio;
    const histPulv = workData?.[1]?.histPulv;
    const histGps = workData?.[4]?.histGps;
    const plantioLength = histPlantio && Array.isArray(histPlantio) ? histPlantio.length : 0;
    const pulvLength = histPulv && Array.isArray(histPulv) ? histPulv.length : 0;
    const gpsLength = histGps && Array.isArray(histGps) ? histGps.length : 0;

    if (plantioLength > 0) {
        return {
            currentHistory: histPlantio,
            isPlantio: true,
            isPulv: false,
            hahField: 'plp_hah',
        };
    }
    if (pulvLength > 0) {
        return {
            currentHistory: histPulv,
            isPlantio: false,
            isPulv: true,
            hahField: 'pup_hah',
        };
    }
    if (gpsLength > 0) {
        return {
            currentHistory: histGps,
            isPlantio: false,
            isPulv: false,
            hahField: 'gps_hah',
        };
    }

    return {
        currentHistory: [],
        isPlantio: true,
        isPulv: false,
        hahField: 'plp_hah',
    };
};

const buildHectareHoraPaths = (data, legends) =>
    buildColoredMapPathsPerWork({
        data,
        getItems: (workData) => resolveHahHistory(workData).currentHistory,
        getShouldIncludeItem: (workData) => {
            const { isPlantio, isPulv } = resolveHahHistory(workData);
            if (isPlantio) return shouldIncludePlantioItem;
            if (isPulv) return shouldIncludePulvItem;
            return null;
        },
        legends,
        getValue: (item) => item.plp_hah ?? item.pup_hah ?? item.gps_hah,
        getCoords: getHistoryPointCoords,
    });

const flattenHahHistory = (data) => {
    const items = [];

    for (const workData of getReportWorksData(data)) {
        const resolved = resolveHahHistory(workData);
        if (resolved.currentHistory.length) {
            items.push(...resolved.currentHistory);
        }
    }

    return { currentHistory: items };
};

const Map_hectarehora = ({ data, implementWidthMeters }) => {
    const [legends, setLegends] = useState([]);
    const [path, setPath] = useState([]);
    const [marker, setMarker] = useState([]);
    const [centerMap, setCenterMap] = useState(null);
    const [loadingMap, setLoadingMap] = useState(true);
    const [sliderValue, setSliderValue] = useState([0, 100]);
    const [realMin, setRealMin] = useState(0);
    const [realMax, setRealMax] = useState(100);
    const isInitialLoadRef = useRef(true);

    const { currentHistory } = useMemo(
        () => flattenHahHistory(data),
        [data]
    );

    const createLegends = (newMin, newMax) => {
        const newLegends = createMapValueLegends(newMin, newMax);
        setLegends(newLegends);
        return newLegends;
    };
    
    
    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    const applyLimits = () => {
        const [newMin, newMax] = sliderValue;
        createLegends(newMin, newMax);
    };  

    const processPath = () => {
        if (!currentHistory || currentHistory.length === 0) {
            return;
        }

        if (!legends || legends.length === 0) {
            return;
        }
        
        setPath(buildHectareHoraPaths(data, legends));
    };

    const handleLegendChange = (legendId) => {
        const legendsCopy = legends.map((legend) =>
            legend.id === legendId ? { ...legend, show: !legend.show } : legend
        );
        setLegends(legendsCopy);
    };

      useEffect(() => {
        isInitialLoadRef.current = true;
        
        if (currentHistory && currentHistory.length > 0) {
            const min = currentHistory.reduce((acc, item) => {
                const value = parseMapNumericValue(item.plp_hah ?? item.pup_hah ?? item.gps_hah);
                return value != null && value > 0 && value < acc ? value : acc;
            }, Number.MAX_VALUE);
            const max = currentHistory.reduce((acc, item) => {
                const value = parseMapNumericValue(item.plp_hah ?? item.pup_hah ?? item.gps_hah) || 0;
                return value > acc ? value : acc;
            }, 0);

            const newRealMin = min !== Number.MAX_VALUE ? min : 0;
            const newRealMax = max;

            setRealMin(newRealMin);
            setRealMax(newRealMax);
            setSliderValue([newRealMin, newRealMax]);
            
            const newLegends = createLegends(newRealMin, newRealMax);
            isInitialLoadRef.current = false;
 
            setPath(buildHectareHoraPaths(data, newLegends));
            setLoadingMap(false);
    
            setMarker([
                { latitude: currentHistory[0].c_lat, longitude: currentHistory[0].c_lng, coordinateType: 'first' },
                { latitude: currentHistory[currentHistory.length - 1].c_lat, longitude: currentHistory[currentHistory.length - 1].c_lng, coordinateType: 'last' }
            ]);
            setCenterMap({ lat: currentHistory[0].c_lat, lng: currentHistory[0].c_lng });
        } else {
            setPath([]);
            setLoadingMap(false);
        }
    }, [currentHistory, data]);

    // Reprocessa caminho sempre que as legendas mudarem (após carregamento inicial)
    useEffect(() => {
        if (isInitialLoadRef.current) {
            return;
        }
        if (legends && legends.length > 0) {
            processPath();
        }
    }, [legends, currentHistory, data]);
    


    return (
        <div style={{width: '100%', alignItems:'center'}}>
            <h3>Variação de hectares por hora durante o trabalho</h3>
            {loadingMap ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
                    <div className="spinner"></div>
                    <BlurCircularTwoTone style={{ fontSize: '40px', marginLeft: '20px' }} />
                </div>
            ) : (
                <>
                    <MapPanel markers={marker} zoomMap={17} centerMap={centerMap} linhas={path} height={600} implementWidthMeters={implementWidthMeters} />
                    <MapLegend legends={legends} onLegendChange={handleLegendChange} />
                    <div className="no-print" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0' }}>
                        <Slider
                            style={{ width: '60%', marginRight: '20px', marginLeft: '24px' }}
                            value={sliderValue}
                            onChange={handleSliderChange}
                            valueLabelDisplay="auto"
                            min={realMin}
                            max={realMax}
                        />
                        <Button 
                            onClick={applyLimits} 
                            style={{ backgroundColor: '#ffca20', color: '#000', borderRadius: '5px' }}
                        >
                            Aplicar Limites
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Map_hectarehora;
